
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, CheckCircle, AlertTriangle, Lock, X, ThumbsUp, ThumbsDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { ContentType } from "@/services/comments/types";

// Define proper types for database responses
interface PublicationEntry {
  pubname: string;
  relname: string;
}

interface ReplicaIdentityEntry {
  relname: string;
  replica_identity: string;
}

interface TableInfoEntry {
  table_name: string;
  replica_identity: string;
}

interface TableConstraintEntry {
  column_name: string;
  constraint_type: string;
}

interface VoteDiagnosticToolProps {
  contentType: ContentType;
}

const VoteDiagnosticTool: React.FC<VoteDiagnosticToolProps> = ({ contentType }) => {
  const [isCheckingConfig, setIsCheckingConfig] = useState(false);
  const [isFixingConfig, setIsFixingConfig] = useState(false);
  const [configStatus, setConfigStatus] = useState<{
    publications?: PublicationEntry[];
    replicaIdentity?: ReplicaIdentityEntry;
    tableInfo?: TableInfoEntry;
    primaryKey?: TableConstraintEntry;
  }>({});
  
  const [testComment, setTestComment] = useState<string | null>(null);
  const [testVote, setTestVote] = useState<{id: string, type: 'up' | 'down'} | null>(null);
  const [realtimeStatus, setRealtimeStatus] = useState<'idle' | 'subscribing' | 'subscribed' | 'error'>('idle');
  const [eventReceived, setEventReceived] = useState<boolean>(false);
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  
  const votesTable = contentType === 'poll' ? 'poll_comment_votes' : 'card_comment_votes';
  const commentsTable = contentType === 'poll' ? 'poll_comments' : 'card_comments';
  
  // Check configuration status
  const checkConfigStatus = async () => {
    setIsCheckingConfig(true);
    try {
      // Check publication status
      const { data: publicationsData, error: pubError } = await supabase.rpc('execute_sql_query', {
        query: `
          SELECT pub.pubname, cls.relname
          FROM pg_publication pub
          JOIN pg_publication_rel pr ON pr.prpubid = pub.oid
          JOIN pg_class cls ON cls.oid = pr.prrelid
          WHERE pub.pubname = 'supabase_realtime'
          AND cls.relname = '${votesTable}'
        `
      });
      
      // Check replica identity
      const { data: replicaIdentityData, error: replicaError } = await supabase.rpc('execute_sql_query', {
        query: `
          SELECT relname, replica_identity
          FROM pg_class c, pg_namespace n
          WHERE c.relnamespace = n.oid
          AND n.nspname = 'public' 
          AND relname = '${votesTable}'
        `
      });
      
      // Get table info
      const { data: tableInfoData, error: tableError } = await supabase.rpc('execute_sql_query', {
        query: `
          SELECT table_name, 
            CASE WHEN EXISTS (
              SELECT 1 
              FROM pg_class c, pg_namespace n
              WHERE c.relnamespace = n.oid
              AND n.nspname = 'public' 
              AND c.relname = '${votesTable}'
              AND c.relreplident = 'f'
            ) THEN 'FULL' ELSE 'DEFAULT' END as replica_identity
          FROM information_schema.tables
          WHERE table_schema = 'public'
          AND table_name = '${votesTable}'
        `
      });
      
      // Check primary key
      const { data: primaryKeyData, error: pkError } = await supabase.rpc('execute_sql_query', {
        query: `
          SELECT column_name, constraint_type
          FROM information_schema.key_column_usage kcu
          JOIN information_schema.table_constraints tc
          ON kcu.constraint_name = tc.constraint_name
          WHERE kcu.table_schema = 'public'
          AND kcu.table_name = '${votesTable}'
          AND tc.constraint_type = 'PRIMARY KEY'
        `
      });
      
      // Process results properly with type safety
      if (publicationsData) {
        try {
          // Safely parse the JSON response for publications
          const publications: PublicationEntry[] = [];
          
          // Handle array or single object response
          const dataArray = Array.isArray(publicationsData) ? publicationsData : [publicationsData];
          
          for (const item of dataArray) {
            // Skip if item is null or not an object
            if (!item || typeof item !== 'object') continue;
            
            // Extract values - handle different possible formats
            let entry: PublicationEntry | null = null;
            
            if (typeof item === 'string') {
              // If item is a JSON string
              try {
                const parsed = JSON.parse(item);
                if (parsed && typeof parsed.pubname === 'string' && typeof parsed.relname === 'string') {
                  entry = { pubname: parsed.pubname, relname: parsed.relname };
                }
              } catch (e) {
                console.error('Failed to parse publication entry', e);
              }
            } else {
              // Try to access as an object with string indexing
              const objItem = item as Record<string, any>;
              if (objItem.pubname && objItem.relname) {
                entry = {
                  pubname: String(objItem.pubname),
                  relname: String(objItem.relname)
                };
              }
            }
            
            if (entry) {
              publications.push(entry);
            }
          }
          
          setConfigStatus(prev => ({ ...prev, publications }));
        } catch (err) {
          console.error('Error parsing publications data:', err);
        }
      }
      
      // Process replica identity data
      if (replicaIdentityData) {
        try {
          // Handle both array and single object responses
          const dataArray = Array.isArray(replicaIdentityData) ? replicaIdentityData : [replicaIdentityData];
          
          if (dataArray.length > 0) {
            const item = dataArray[0];
            if (item && typeof item === 'object') {
              const objItem = item as Record<string, any>;
              
              const replicaIdentity: ReplicaIdentityEntry = {
                relname: String(objItem.relname || ''),
                replica_identity: String(objItem.replica_identity || '')
              };
              
              setConfigStatus(prev => ({ ...prev, replicaIdentity }));
            }
          }
        } catch (err) {
          console.error('Error parsing replica identity data:', err);
        }
      }
      
      // Process table info data
      if (tableInfoData) {
        try {
          // Handle both array and single object responses
          const dataArray = Array.isArray(tableInfoData) ? tableInfoData : [tableInfoData];
          
          if (dataArray.length > 0) {
            const item = dataArray[0];
            if (item && typeof item === 'object') {
              const objItem = item as Record<string, any>;
              
              const tableInfo: TableInfoEntry = {
                table_name: String(objItem.table_name || ''),
                replica_identity: String(objItem.replica_identity || '')
              };
              
              setConfigStatus(prev => ({ ...prev, tableInfo }));
            }
          }
        } catch (err) {
          console.error('Error parsing table info data:', err);
        }
      }
      
      // Process primary key data
      if (primaryKeyData) {
        try {
          // Handle both array and single object responses
          const dataArray = Array.isArray(primaryKeyData) ? primaryKeyData : [primaryKeyData];
          
          if (dataArray.length > 0) {
            const item = dataArray[0];
            if (item && typeof item === 'object') {
              const objItem = item as Record<string, any>;
              
              const primaryKey: TableConstraintEntry = {
                column_name: String(objItem.column_name || ''),
                constraint_type: String(objItem.constraint_type || '')
              };
              
              setConfigStatus(prev => ({ ...prev, primaryKey }));
            }
          }
        } catch (err) {
          console.error('Error parsing primary key data:', err);
        }
      }
      
      if (pubError || replicaError || tableError || pkError) {
        console.error("Errors checking configuration:", { pubError, replicaError, tableError, pkError });
        toast.error("Error checking configuration");
      }
    } catch (error) {
      console.error('Error checking configuration status:', error);
      toast.error('Failed to check configuration');
    } finally {
      setIsCheckingConfig(false);
    }
  };
  
  // Fix configuration
  const fixConfiguration = async () => {
    setIsFixingConfig(true);
    try {
      // Add table to realtime publication if needed
      if (!configStatus.publications || configStatus.publications.length === 0) {
        const { error: pubError } = await supabase.rpc('execute_sql_query', {
          query: `
            ALTER PUBLICATION supabase_realtime
            ADD TABLE public.${votesTable};
          `
        });
        
        if (pubError) {
          console.error("Error updating publication:", pubError);
          toast.error("Error updating publication");
          return;
        }
      }
      
      // Set replica identity to FULL if needed
      if (!configStatus.replicaIdentity || configStatus.replicaIdentity.replica_identity !== 'f') {
        const { error: replicaError } = await supabase.rpc('execute_sql_query', {
          query: `
            ALTER TABLE public.${votesTable}
            REPLICA IDENTITY FULL;
          `
        });
        
        if (replicaError) {
          console.error("Error setting replica identity:", replicaError);
          toast.error("Error setting replica identity");
          return;
        }
      }
      
      // Add primary key if needed
      if (!configStatus.primaryKey) {
        const { error: pkError } = await supabase.rpc('execute_sql_query', {
          query: `
            ALTER TABLE public.${votesTable}
            ADD PRIMARY KEY (id);
          `
        });
        
        if (pkError) {
          console.error("Error adding primary key:", pkError);
          toast.error("Error adding primary key");
          return;
        }
      }
      
      toast.success("Configuration updated successfully");
      // Re-check configuration after fixing
      setTimeout(checkConfigStatus, 1000);
    } catch (error) {
      console.error('Error fixing configuration:', error);
      toast.error('Failed to fix configuration');
    } finally {
      setIsFixingConfig(false);
    }
  };
  
  // Create a test comment
  const createTestComment = async () => {
    setIsCreatingComment(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      
      if (!userId) {
        toast.error("You must be logged in to create a test comment");
        return;
      }
      
      // Generate a unique content for the test comment
      const testContent = `Diagnostic test comment ${new Date().toISOString()}`;
      
      // Insert the comment with proper table structure and typing
      if (contentType === 'poll') {
        const { data, error } = await supabase
          .from('poll_comments')
          .insert({
            content: testContent,
            user_id: userId,
            poll_id: '2f9e6a4e-7b5c-4d8a-9f3b-1c2e4a6d8b9c' // Example poll ID
          })
          .select('id')
          .single();
        
        if (error) {
          console.error('Error creating test comment:', error);
          toast.error('Failed to create test comment');
          return;
        }
        
        setTestComment(data.id);
      } else {
        const { data, error } = await supabase
          .from('card_comments')
          .insert({
            content: testContent,
            user_id: userId,
            card_id: '2f9e6a4e-7b5c-4d8a-9f3b-1c2e4a6d8b9c' // Example card ID
          })
          .select('id')
          .single();
        
        if (error) {
          console.error('Error creating test comment:', error);
          toast.error('Failed to create test comment');
          return;
        }
        
        setTestComment(data.id);
      }
      
      toast.success("Test comment created successfully");
    } catch (error) {
      console.error('Error creating test comment:', error);
      toast.error('Failed to create test comment');
    } finally {
      setIsCreatingComment(false);
    }
  };
  
  // Vote on the test comment using edge function
  const voteOnTestComment = async (voteType: 'up' | 'down') => {
    if (!testComment) {
      toast.error("Create a test comment first");
      return;
    }
    
    setIsVoting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      
      if (!userId) {
        toast.error("You must be logged in to vote");
        return;
      }
      
      // Use edge function to insert vote
      const { data, error } = await supabase.functions.invoke('insert_comment_vote', {
        body: JSON.stringify({
          p_comment_id: testComment,
          p_user_id: userId,
          p_anonymous_id: null,
          p_vote_type: voteType,
          p_table_name: votesTable
        })
      });
      
      if (error) {
        console.error('Error voting:', error);
        toast.error('Failed to submit vote');
        return;
      }
      
      // Check if we need to toggle an existing vote
      if (testVote && testVote.type === voteType) {
        // If the same vote type, we're toggling it off
        setTestVote(null);
        toast.success(`${voteType === 'up' ? 'Upvote' : 'Downvote'} removed`);
      } else {
        // Otherwise we're setting a new vote
        if (data && data.id) {
          setTestVote({ id: data.id, type: voteType });
          toast.success(`${voteType === 'up' ? 'Upvoted' : 'Downvoted'} successfully`);
        } else {
          console.warn('No ID returned from vote operation', data);
        }
      }
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote');
    } finally {
      setIsVoting(false);
    }
  };
  
  // Subscribe to realtime events
  useEffect(() => {
    if (!testComment) return;
    
    setRealtimeStatus('subscribing');
    
    // Create a channel to listen for vote changes
    const channel = supabase.channel('vote-diagnostics')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: votesTable, filter: `comment_id=eq.${testComment}` },
        (payload) => {
          console.log('Realtime vote payload:', payload);
          setEventReceived(true);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setRealtimeStatus('subscribed');
          console.log('Subscribed to vote events for comment', testComment);
        } else if (status === 'CHANNEL_ERROR') {
          setRealtimeStatus('error');
          console.error('Error connecting to realtime channel');
        }
      });
    
    // Clean up on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [testComment, votesTable]);
  
  // Run configuration check on mount
  useEffect(() => {
    checkConfigStatus();
  }, []);
  
  // Get alert variant based on status
  const getAlertVariant = (isOk: boolean) => {
    return isOk ? "default" : "destructive";
  };
  
  const publicationOk = configStatus.publications && configStatus.publications.length > 0;
  const replicaIdentityOk = configStatus.replicaIdentity?.replica_identity === 'f';
  const primaryKeyOk = !!configStatus.primaryKey;
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Vote Diagnostics</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={checkConfigStatus} 
          disabled={isCheckingConfig}
        >
          {isCheckingConfig ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Refresh Status
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4">
            <h4 className="font-medium mb-2">Configuration Status</h4>
            <div className="space-y-3">
              <Alert variant={getAlertVariant(publicationOk)}>
                <div className="flex items-center">
                  {publicationOk ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <AlertTitle className="ml-2">Publication Status</AlertTitle>
                </div>
                <AlertDescription className="ml-6 text-xs">
                  {publicationOk 
                    ? `Table ${votesTable} is in supabase_realtime publication`
                    : `Table ${votesTable} is NOT in supabase_realtime publication`}
                </AlertDescription>
              </Alert>
              
              <Alert variant={getAlertVariant(replicaIdentityOk)}>
                <div className="flex items-center">
                  {replicaIdentityOk ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <AlertTitle className="ml-2">Replica Identity</AlertTitle>
                </div>
                <AlertDescription className="ml-6 text-xs">
                  {replicaIdentityOk
                    ? `Table ${votesTable} has REPLICA IDENTITY FULL`
                    : `Table ${votesTable} needs REPLICA IDENTITY FULL`}
                </AlertDescription>
              </Alert>
              
              <Alert variant={getAlertVariant(primaryKeyOk)}>
                <div className="flex items-center">
                  {primaryKeyOk ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <AlertTitle className="ml-2">Primary Key</AlertTitle>
                </div>
                <AlertDescription className="ml-6 text-xs">
                  {primaryKeyOk
                    ? `Table ${votesTable} has a PRIMARY KEY on id column`
                    : `Table ${votesTable} needs PRIMARY KEY on id column`}
                </AlertDescription>
              </Alert>
              
              {(!publicationOk || !replicaIdentityOk || !primaryKeyOk) && (
                <Button 
                  onClick={fixConfiguration}
                  disabled={isFixingConfig}
                  variant="default"
                  size="sm"
                  className="w-full mt-2"
                >
                  {isFixingConfig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Fix Configuration
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <h4 className="font-medium mb-2">Realtime Test</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <Badge variant="outline">
                    {testComment ? "Comment Created" : "No Test Comment"}
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createTestComment}
                  disabled={isCreatingComment || !!testComment}
                >
                  {isCreatingComment && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Test Comment
                </Button>
              </div>
              
              {testComment && (
                <>
                  <Alert variant={realtimeStatus === 'subscribed' ? 'default' : 'destructive'}>
                    <div className="flex items-center">
                      {realtimeStatus === 'subscribing' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : realtimeStatus === 'subscribed' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-red-600" />
                      )}
                      <AlertTitle className="ml-2">Realtime Status</AlertTitle>
                    </div>
                    <AlertDescription className="ml-6 text-xs">
                      {realtimeStatus === 'subscribing' ? 'Connecting to realtime channel...' : 
                       realtimeStatus === 'subscribed' ? 'Subscribed to realtime updates' : 
                       'Failed to connect to realtime channel'}
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant={testVote?.type === 'up' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => voteOnTestComment('up')}
                      disabled={isVoting || realtimeStatus !== 'subscribed'}
                      className="flex-1"
                    >
                      {isVoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ThumbsUp className="mr-2 h-4 w-4" />}
                      {testVote?.type === 'up' ? 'Remove Upvote' : 'Upvote'}
                    </Button>
                    <Button
                      variant={testVote?.type === 'down' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => voteOnTestComment('down')}
                      disabled={isVoting || realtimeStatus !== 'subscribed'}
                      className="flex-1"
                    >
                      {isVoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ThumbsDown className="mr-2 h-4 w-4" />}
                      {testVote?.type === 'down' ? 'Remove Downvote' : 'Downvote'}
                    </Button>
                  </div>
                  
                  <Alert variant={eventReceived ? "default" : "destructive"}>
                    <div className="flex items-center">
                      {eventReceived ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Lock className="h-4 w-4 text-amber-600" />
                      )}
                      <AlertTitle className="ml-2">Realtime Events</AlertTitle>
                    </div>
                    <AlertDescription className="ml-6 text-xs">
                      {eventReceived 
                        ? 'Realtime event received! Your config is working correctly.'
                        : 'No realtime events received yet. Try voting on the comment.'}
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VoteDiagnosticTool;
